import { Camera } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";

function initialsFromName(name: string): string {
  const [first, second] = name.trim().split(/\s+/);
  return `${first?.[0] ?? ""}${second?.[0] ?? ""}`.toUpperCase() || "?";
}

function memberSinceLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

interface ProfileHeaderCardProps {
  name: string;
  avatarUrl: string | null;
  memberSince: string;
  bio?: string;
  itemsListedCount?: number;
  /** Shows the avatar-edit affordance — only ever true on the owner's own
   * `/profile`, never on `/profile/:userId`'s read-only view of someone
   * else. */
  isOwn?: boolean;
  onEditAvatar?: () => void;
  isUploadingAvatar?: boolean;
}

/**
 * docs/ui/component-inventory.md's `ProfileHeaderCard`: avatar + stat row
 * at the top of a Profile page. Drops the doc's "verified badge" and
 * "Rating" stat — there's no real KYC/verification flag to back a badge
 * with (verification-status isn't being built) and no reviews system to
 * back a rating with; a fake or zero value would be worse than omitting
 * both, the same call already made for `SellerCard`'s star rating.
 */
export function ProfileHeaderCard({
  name,
  avatarUrl,
  memberSince,
  bio,
  itemsListedCount,
  isOwn,
  onEditAvatar,
  isUploadingAvatar,
}: ProfileHeaderCardProps) {
  return (
    <div className="flex flex-col items-center gap-4 rounded-lg border border-border bg-card p-6 text-center sm:flex-row sm:text-left">
      <div className="relative">
        <Avatar size="xl">
          {avatarUrl && <AvatarImage src={avatarUrl} alt="" />}
          <AvatarFallback className="text-h2">{initialsFromName(name)}</AvatarFallback>
        </Avatar>
        {isOwn && (
          <button
            type="button"
            onClick={onEditAvatar}
            disabled={isUploadingAvatar}
            aria-label="Change profile photo"
            className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full border-2 border-background bg-primary text-primary-foreground disabled:opacity-50"
          >
            <Camera className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        )}
      </div>

      <div className="flex-1 space-y-1">
        <h1 className="text-h2 text-foreground">{name}</h1>
        <p className="text-body-sm text-muted-foreground">
          Member since {memberSinceLabel(memberSince)}
        </p>
        {bio && <p className="text-body text-foreground">{bio}</p>}
        {itemsListedCount !== undefined && (
          <p className="text-body-sm text-muted-foreground">
            <span className="font-medium text-foreground">{itemsListedCount}</span> item
            {itemsListedCount === 1 ? "" : "s"} listed
          </p>
        )}
      </div>
    </div>
  );
}

export function ProfileHeaderCardSkeleton() {
  return (
    <div className="flex flex-col items-center gap-4 rounded-lg border border-border bg-card p-6 sm:flex-row">
      <Skeleton className="h-24 w-24 rounded-full" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-4 w-1/4" />
      </div>
    </div>
  );
}
